import { useNavigation } from "@react-navigation/native";
import {
  Calendar,
  CheckCircle,
  Info,
  User,
  Users,
} from "@tamagui/lucide-icons";
import React from "react";
import { type GetThemeValueForKey, Text, XStack } from "tamagui";
import { Body } from "../../../ui/Body";
import type { IconProp } from "../../../ui/icons";
import type { BusinessPlanModel } from "../../plan/BusinessPlanModel";
import {
  type BusinessFeedMention,
  BusinessFeedMentionType,
} from "../BusinessFeedItem";

// ✅ Inline util to parse mentions from raw text + mention metadata
export function parseFeedMentions(
  text: string,
  mentions: BusinessFeedMention[] = [],
): Array<
  | { type: "text"; content: string }
  | { type: "mention"; content: string; mention: BusinessFeedMention }
> {
  if (!mentions.length) return [{ type: "text", content: text }];

  // Collect all mention occurrences with their positions
  const occurrences: Array<{
    start: number;
    end: number;
    mention: BusinessFeedMention;
  }> = [];

  for (const m of mentions) {
    const marker = m.name;
    let searchStart = 0;

    while (true) {
      const idx = text.indexOf(marker, searchStart);
      if (idx === -1) break;

      const start = idx;
      const end = idx + marker.length;

      occurrences.push({ start, end, mention: m });

      searchStart = end; // continue after this match
    }
  }

  // Sort matches by where they appear
  occurrences.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first one)
  const filtered: typeof occurrences = [];
  let lastEnd = -1;
  for (const occ of occurrences) {
    if (occ.start >= lastEnd) {
      filtered.push(occ);
      lastEnd = occ.end;
    }
  }

  // Build the result
  const result: Array<
    | { type: "text"; content: string }
    | { type: "mention"; content: string; mention: BusinessFeedMention }
  > = [];

  let cursor = 0;
  for (const occ of filtered) {
    if (occ.start > cursor) {
      result.push({ type: "text", content: text.slice(cursor, occ.start) });
    }

    result.push({
      type: "mention",
      content: text.slice(occ.start, occ.end),
      mention: occ.mention,
    });

    cursor = occ.end;
  }

  if (cursor < text.length) {
    result.push({ type: "text", content: text.slice(cursor) });
  }

  return result;
}

// ✅ Styled inline “pill” mention
const MentionPill = ({
  mention,
  content,
  onPress,
}: {
  mention: BusinessFeedMention;
  content: string;
  onPress?: (mention: BusinessFeedMention) => void;
}) => {
  const colorMap: Record<
    BusinessFeedMentionType,
    GetThemeValueForKey<"color">
  > = {
    user: "$color.hot",
    group: "$color.info",
    task: "$color.success",
    schedule: "$color.primary",
    info: "$color.secondary",
  };

  const bgColorMap: Record<
    BusinessFeedMentionType,
    GetThemeValueForKey<"color">
  > = {
    user: "rgba(239,95,13,0.1)",
    group: "rgba(0,145,255,0.1)",
    task: "rgba(48,164,108,0.1)",
    schedule: "rgba(255,216,78,0.15)",
    info: "rgba(0,0,0,0.05)",
  };

  const iconMap: Record<BusinessFeedMentionType, IconProp> = {
    user: User,
    group: Users,
    task: CheckCircle,
    schedule: Calendar,
    info: Info,
  };

  const Icon = iconMap[mention.type];

  return (
    <Body
      backgroundColor={bgColorMap[mention.type]}
      borderRadius={100}
      paddingHorizontal={6}
      marginHorizontal={1}
      display="flex"
      alignItems={"center"}
      fontWeight="600"
      cursor={onPress ? "pointer" : undefined}
      onPress={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onPress?.(mention);
      }}
    >
      <Body color={colorMap[mention.type]} marginRight={5}>
        {content}
      </Body>

      <Icon
        style={{ minWidth: 16 }}
        color={colorMap[mention.type]}
        size={"$size.small"}
      />
    </Body>
  );
};

export const FeedTextWithMentions = ({
  text,
  mentions,
  businessPlan,
}: {
  text: string;
  mentions: BusinessFeedMention[];
  businessPlan: BusinessPlanModel;
}) => {
  const navigation = useNavigation();
  const segments = parseFeedMentions(text, mentions ?? []);

  const onMentionPress = React.useCallback(
    (mention: BusinessFeedMention) => {
      switch (mention.type) {
        case BusinessFeedMentionType.User:
          businessPlan.setShowUserInfo(mention.uid);
          break;

        case BusinessFeedMentionType.Group:
          navigation.navigate("BusinessMembers", {
            businessUid: businessPlan.uid,
          });
          break;

        case BusinessFeedMentionType.Task:
          navigation.navigate("BusinessTasksModule", {
            businessUid: businessPlan.uid,
            businessModuleUid: mention.uid,
          });
          break;

        case BusinessFeedMentionType.Schedule:
          navigation.navigate("BusinessScheduleItem", {
            businessUid: businessPlan.uid,
            itemUid: mention.uid,
          });
          break;

        case BusinessFeedMentionType.Info:
          navigation.navigate("BusinessSectionModule", {
            businessUid: businessPlan.uid,
            itemUid: mention.uid,
          });
          break;
      }
    },
    [businessPlan, navigation],
  );

  return (
    <XStack flexWrap="wrap">
      {segments.map((seg, idx) => {
        if (seg.type === "text") {
          return <Text key={idx}>{seg.content}</Text>;
        }

        const { mention } = seg;
        return (
          <MentionPill
            key={idx}
            mention={mention}
            content={seg.content}
            onPress={onMentionPress}
          />
        );
      })}
    </XStack>
  );
};
